import {ADataGrid, ADataGridColumn, ADataGridFilter, ASectionTitle, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import React, {useCallback, useEffect, useMemo, useState, useRef} from "react";
import {extractFiltersFromColumns} from "../../../../Utils/DataUitils";
import {useSnackbar} from "notistack";
import {format} from "date-fns";
import { Box, Button, IconButton, Tooltip, Chip, Typography } from "@mui/material";
import { AddCircle, Delete, DeleteOutlined, Edit } from "@mui/icons-material";
import { SideBar } from "./sidebar/SideBar";
import { TeleObjectMapSearchReactQuery, TeleObjectMapDeleteReactQuery } from "../PowerStationReactQuery";
import { log } from "console";
import id from "date-fns/esm/locale/id/index.js";
import ConfirmDialog from "../../../../Shared/Components/ConfirmDialog";

interface Props {
	powerStationId: number
	powerStationName: string | null
	onSaveTeleObjectMapTrackersCurrents?: (data: Array<any>) => void
}

interface TeleObjectMapItem {
    id: number;
    name: string;
    teleObjectFrom: {
        id: number;
        name: string;
		teleObjectId: number;
    };
    teleObjectTo: {
        id: number;
        teleObjectConfig: {
			id: number;
			name: string;
		};
    };
}

export type ActionConfig = {
	actionName: string;
	disabled: boolean;
};

interface TeleObjectMapRow {
	id?: number;
	name?: string;
	createdAt?: string;
	mappingType?: string;
	actionsConfig?: Array<ActionConfig>;
	itemMap?: React.ReactNode;
	teleObjectMapItemList?: any;
}

export interface TeleObjectMapTrackersCurrents {
	count: number;
	equipmentId: number;
	equipmentName: string;
	equipmentTypeId: number;
	skidId: number | null;
	skidName: string | null;
	teleObjectConcatenatedName: string;
	teleObjectConfigDataTypeReference: number;
	teleObjectConfigName: string;
	teleObjectId: number;
}

const initColumnsData = [
     {
        name: 'id',
        label: 'ID',
        align: 'left',
		minWidth: 40,
        width: '2px',
		// maxWidth: 20,
        breakpoints: {
            xs: false,
            sm: false,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'id', condition: 'equals', value: '', sort: 'none'}

    },
    {
		name: 'trackerName',
		label: 'Tracker',
		align: 'left',
		minWidth: 2,
		maxWidth: 5,
		breakpoints: {
			xs: true,
			sm: true,
			md: true,
			lg: true,
			xl: true
		},
		filter: { name: 'trackerName', condition: 'contains', value: '', sort: 'asc' }
	},
    {	
		name: 'itemMap', 
		label: 'Mapeamento de Correntes',
		align: 'left',
		minWidth: 200,
		width: '400px',
		breakpoints: {
			xs: true,
			sm: true,
			md: true,
			lg: true,
			xl: true
		},
		filter: { name: 'itemMap', condition: null, value: null, sort: null }
	},
] as ADataGridColumn[];

export const PowerStationFormEditTeleObjectMapTrackersCurrentsPage = ({
	powerStationId,
	onSaveTeleObjectMapTrackersCurrents
}: Props) => {
	const {enqueueSnackbar} = useSnackbar();
    const columns = useMemo(() => initColumnsData, []);
	const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(columns), 0, 100));

	const teleObjectMapGrid = TeleObjectMapSearchReactQuery();
	const teleObjectMapDelete = TeleObjectMapDeleteReactQuery(pagedSearchParams, powerStationId);
	const [rows, setRows] = useState<Array<TeleObjectMapRow>>([]);
	const [totalOfRecords, setTotalOfRecords] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [selectedTeleObjectMap, setSelectedTeleObjectMap] = useState<any>(null);

	const teleObjectMapIdsExist = useMemo(() => {
		return rows?.flatMap((row) => 
			row.teleObjectMapItemList?.[0]?.teleObjectFrom?.teleObjectId) ?? [];
	}, [rows]);


	const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
		let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
		setPagedSearchParams(newPageParams);
	}, []);

	const processSearchResponse = (response: any) => {
		const result = response;
		setTotalOfRecords(result?.totalOfRecords ?? 0);
		const mappedRows:TeleObjectMapRow[] = (result?.data ?? []).map((item: any) => {
			let formattedCreatedAt: string | undefined = item?.createdAt;
			if (item?.createdAt) {
				try {
					formattedCreatedAt = format(new Date(item.createdAt), "dd/MM/yyyy HH:mm");
				} catch (error) {
					formattedCreatedAt = item?.createdAt;
				}
			}
			return {
				id: item?.id,
				trackerName: item?.name,
				mappingType: item?.mappingType,
				createdAt: formattedCreatedAt,
				actionsConfig: [],
				teleObjectMapItemList: item?.teleObjectMapItemList,
				itemMap: (
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							maxHeight: 85,
							overflowY: 'auto',
							pt: '16px',
							/* WebKit-based browsers */
							'&::-webkit-scrollbar': { width: 8 },
							'&::-webkit-scrollbar-track': { background: 'transparent' },
							'&::-webkit-scrollbar-thumb': { backgroundColor: '#BDBDBD', borderRadius: 6 },
							'&::-webkit-scrollbar-button': { display: 'none' },
							'&::-webkit-scrollbar-arrow': { background: 'transparent' },
							/* Firefox */
							scrollbarWidth: 'thin',
							scrollbarColor: '#BDBDBD transparent',
						}}
					>
						{item?.teleObjectMapItemList?.map((mapItem: TeleObjectMapItem) => {
							
							const toName = mapItem?.name || "";
							return (
								<Chip
									key={mapItem?.id ?? toName}
									label={toName}
									variant="outlined"
									color="primary"
									size="small"
									sx={{ mr: 1, mb: 1, width: '130px' }}
								/>
							);
						}) ?? null}
					</Box>
				)
			} as TeleObjectMapRow;
		});

		setRows(mappedRows);
		onSaveRef.current?.(result?.data ?? []);
	};
	
	const onSaveRef = useRef<typeof onSaveTeleObjectMapTrackersCurrents>(onSaveTeleObjectMapTrackersCurrents);

	useEffect(() => {
		onSaveRef.current = onSaveTeleObjectMapTrackersCurrents;
	}, [onSaveTeleObjectMapTrackersCurrents]);

	useEffect(() => {
		
		if (powerStationId == null) {
			setRows([]);
			setTotalOfRecords(0);
			return;
		}

		setLoading(true);

		teleObjectMapGrid.mutate({pagedSearchParams, powerStationId}, {
			onSuccess: (response: any) => {
				processSearchResponse(response);
				setLoading(false);
			},
			onError: (error: any) => {
				console.error("Erro ao buscar o mapeamento de Tele Objetos.", error);
				enqueueSnackbar("Erro ao buscar o mapeamento de Tele Objetos.", {variant: "error"});
				setLoading(false);
			}
		});
	}, [pagedSearchParams]);

    const handleOnEdit = (value: any) => {
		setOpenModal(true)
		setSelectedTeleObjectMap(value);
    }


    const handleOnClosed = (value: any) => {		
		setOpenModal(false)
		setSelectedTeleObjectMap(null);
		teleObjectMapGrid.mutate({ pagedSearchParams, powerStationId }, {
			onSuccess: (response: any) => {
				processSearchResponse(response);
			}
		});
		
    }
    const handleOnDelete = (value: any) => {		
		const id = value?.id;
		if (!id) {
			enqueueSnackbar("ID inválido para exclusão.", { variant: "error" });
			return;
		}

		setSelectedTeleObjectMap(value);
		setOpenDialog(true);		
    }	

	const handleDialogConfirm = async () => {
		if(selectedTeleObjectMap?.id) {
			teleObjectMapDelete.mutate(selectedTeleObjectMap?.id, {
				onSuccess: () => {
					setRows((prevRows) => prevRows.filter((row) => row.id !== selectedTeleObjectMap?.id));
					enqueueSnackbar("Mapeamento excluído com sucesso.", { variant: "success" });
					setSelectedTeleObjectMap(null);
					handleDialogToggle();
				}
			});
		}
    }

    const handleDialogToggle = () => {
        setOpenDialog(!openDialog);
    }

    const handleDialogClose = () => {
        handleDialogToggle();
    }
	

	return (
		<Box style={{padding: 16}}>
            <ASectionTitle
				styleContainer={{paddingBottom: 24}}
                titleLabel={"Mapeamento de Trackers e Correntes de Strings"}
                contentChildren={
					<Button
						size={"small"}
						variant="contained"
						onClick={() => handleOnEdit(null)}
						startIcon={<AddCircle />}>
						Novo Mapeamento
					</Button>
}
            />

			<ADataGrid
				hideSelection={true}
				columns={columns}
				rows={rows}
				page={pagedSearchParams.page}
				rowsPerPage={pagedSearchParams.size}
				loading={loading}
                actions={
					<>
						<IconButton name="edit" size={"small"} aria-label="Edit" onClick={(value) => handleOnEdit(value)}>
							<Tooltip title="Editar">
								<Edit />
							</Tooltip>
						</IconButton>
						<IconButton name="delete" size={"small"} aria-label="Delete" onClick={(value) => handleOnDelete(value)}>
							<Tooltip title="Excluir">
								<Delete />
							</Tooltip>
						</IconButton>
					</>
				}
				totalOfRecords={totalOfRecords}
				onFiltersChange={handleOnFiltersChange}
				disableActionsHeaderIcons={true}
			/>
			<SideBar 
				open={openModal}
				onCloseModal={handleOnClosed}
				onSave={onSaveTeleObjectMapTrackersCurrents}
				data={rows}
				id={powerStationId}
				selectedTeleObjectMapEdit={selectedTeleObjectMap}
				teleObjectMapIdsExist={teleObjectMapIdsExist}
			/>
			<ConfirmDialog
				title={<Box sx={{alignItems: 'flex-end', display: 'flex', flexDirection: 'row', gap: 1}}>
					<DeleteOutlined color="info" fontSize="large" /> 
				<Typography variant="h2" fontSize={18}>
					Tem certeza que deseja excluir esse mapeamento?
					
				</Typography>
				</Box>}
				description={<>Essa ação encerrará a relação entre as correntes e o tracker <b>{selectedTeleObjectMap?.trackerName}</b> no sistema.</>}
				confirmLabel={'Remover mapeamento'}
				open={openDialog}
				variant={'info'}
				handleConfirm={handleDialogConfirm}
				handleClose={handleDialogClose}
			/>
		</Box>
	)
}
